import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  useTheme,
  Button,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Public as PublicIcon,
  Computer as ComputerIcon,
  Language as LanguageIcon,
  Dns as DnsIcon,
} from '@mui/icons-material';
import jobseekerapi from '../../../../services/jobseekerApi';

// Types
interface Visitor {
  _id?: string;
  ip_address: string;
  location: string;
  os: string;
  browser: string;
  source?: string;
  is_talk_with_ai?: boolean;
  is_signup?: boolean;
  is_signin?: boolean;
  createdAt?: string;
  [key: string]: any;
}

const Visitors: React.FC = () => {
  const theme = useTheme();

  // State variables
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRows, setTotalRows] = useState<number>(0);

  // Fetch visitors on component mount and when filters/pagination changes
  useEffect(() => {
    fetchVisitors();
  }, [page, rowsPerPage, searchQuery]);

  // Function to fetch visitors
  const fetchVisitors = async () => {
    setLoading(true);
    setError(null);

    try {
      // startToken is page number (1-indexed), endToken is number of records per page
      const startToken = page + 1;
      const endToken = rowsPerPage;

      const response = await jobseekerapi.post('/jobseeker/trackUserVisitors', {
        startToken,
        endToken,
        search: searchQuery,
      });

      if (response?.data?.status) {
        setVisitors(response.data.data || []);
        setTotalRows(response.data.record_count || 0);
      } else {
        // If API returns success: false but usually we might handle empty list or specific error
        // assuming standard response structure
        setVisitors(response.data.data || []); 
        setTotalRows(response.data.record_count || 0);
      }
    } catch (err: any) {
      console.error('Error fetching visitors:', err);
      setError(err?.response?.data?.message || 'Failed to fetch visitors');
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Reset search
  const resetFilters = () => {
    setSearchQuery('');
    setPage(0);
  };

  // Format date
  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Visitors
        </Typography>
      </Box>

      {/* Search Filter */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <TextField
          label="Search Visitors"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by IP, location..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>
          Reset
        </Button>
      </Paper>

      {/* Visitors Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DnsIcon fontSize="small" color="action" />
                    IP Address
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon fontSize="small" color="action" />
                    Location
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ComputerIcon fontSize="small" color="action" />
                    OS
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon fontSize="small" color="action" />
                    Browser
                  </Box>
                </TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Talk With AI</TableCell>
                <TableCell>Signup</TableCell>
                <TableCell>Signin</TableCell>
                <TableCell>Visited On</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'error.main' }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : visitors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    {searchQuery ? 'No visitors found matching your search' : 'No visitors found'}
                  </TableCell>
                </TableRow>
              ) : (
                visitors.map((visitor, index) => (
                  <TableRow key={visitor._id || index} hover>
                    <TableCell>
                      <Chip 
                        icon={<DnsIcon style={{ fontSize: 16 }} />} 
                        label={visitor.ip_address || 'N/A'} 
                        variant="outlined" 
                        size="small"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visitor.location || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visitor.os || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visitor.browser || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visitor.source || 'Website'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={visitor.is_talk_with_ai ? "Yes" : "No"} 
                        color={visitor.is_talk_with_ai ? "primary" : "default"}
                        size="small" 
                        variant={visitor.is_talk_with_ai ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={visitor.is_signup ? "Yes" : "No"} 
                        color={visitor.is_signup ? "success" : "default"}
                        size="small" 
                        variant={visitor.is_signup ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={visitor.is_signin ? "Yes" : "No"} 
                        color={visitor.is_signin ? "info" : "default"}
                        size="small" 
                        variant={visitor.is_signin ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(visitor.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default Visitors;
